import { Debounce } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/Debounce.js";

/**
 * The FigureElement class is used to apply
 * additional behaviour to figure elements
 */
export class FigureElement {
    private static instance: FigureElement;

    private static readonly TOPBAR_HEIGHT = "--topbar-height";
    private static readonly CAPTION_HEIGHT = "--caption-height";

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        const figureElements = this.figureElements;

        this.syncCaptionWidths(figureElements);
    }

    /**
     * Method to instantiate the
     * pre on the current page
     *
     * @returns {FigureElement}
     */
    public static use(): FigureElement {
        if (!FigureElement.instance) {
            FigureElement.instance = new FigureElement();
        }
        return FigureElement.instance;
    }

    /**
     * Selects the pre elements
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    protected get figureElements(): NodeListOf<HTMLElement> {
        return document.querySelectorAll("figure");
    }

    /**
     * Method to synchronize the caption widths
     * with the figure element surrounding them
     *
     * @param {NodeListOf<HTMLElement>} elements
     * @returns {void}
     */
    private syncCaptionWidths(elements: NodeListOf<HTMLElement>): void {
        // Create a resize observer
        const observer = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                this.syncCaptionWidth(entry.target as HTMLElement);
            });
        });

        // Observe all figure elements and apply
        // an initial synchronization of the width
        elements.forEach((element) => {
            observer.observe(element);
            this.syncCaptionWidth(element);
        });

        // Synchronize all captions during resizing
        window.addEventListener(
            "resize",
            Debounce.simple(() => {
                this.syncAllCaptions(elements);
            }, 200),
        );
    }

    /**
     * Method to synchronize the width of the caption with the
     * width of the figure elements that is wrapping it
     *
     * @param {HTMLElement} element
     * @returns {void}
     */
    private syncCaptionWidth(element: HTMLElement): void {
        const caption = element.querySelector<HTMLElement>("figcaption");
        if (!caption) return;

        // Hide the caption to prevent any influence on
        // the width of the figure surrounding it
        caption.hidden = true;

        // Force reflow to ensure the style is
        // applied by accessing offsetWidth
        void caption.offsetWidth;

        // Evaluate the width of the figure
        const content = element.querySelector(
            ":scope > div, :scope > img",
        ) as HTMLElement;
        const contentWidth = content?.clientWidth ?? element.clientWidth;

        // Apply the width of the figure to
        // the caption and show the caption
        caption.style.width = `${contentWidth}px`;
        caption.hidden = false;

        // Evaluate the caption height for styling purposes
        const topbar = element.querySelector<HTMLElement>("div[topbar]");
        if (topbar) {
            const style = window.getComputedStyle(topbar);
            const marginBottom = parseFloat(style.marginBottom);
            element.style.setProperty(
                FigureElement.TOPBAR_HEIGHT,
                `${Math.max(0, topbar.clientHeight + marginBottom)}px`,
            );
        }

        // Evaluate the caption height for styling purposes
        {
            const style = window.getComputedStyle(caption);
            const marginTop = parseFloat(style.marginTop);
            element.style.setProperty(
                FigureElement.CAPTION_HEIGHT,
                `${Math.max(0, caption.clientHeight + marginTop)}px`,
            );
        }
    }

    /**
     * Method to synchronize all captions
     * within figure elements
     *
     * @param {NodeListOf<HTMLElement>} elements
     * @returns {void}
     */
    private syncAllCaptions(elements: NodeListOf<HTMLElement>): void {
        elements.forEach((element) => {
            this.syncCaptionWidth(element);
        });
    }
}
